import { useState } from 'react';
import { api } from '../../services/api';
import { Modal } from '../common/Modal';
import { PLATFORM_INFO } from '../../types';
import type { Platform } from '../../types';

interface CreateDraftModalProps {
  contentId: string;
  onClose: () => void;
  onCreated: () => void;
}

const PLATFORMS: Platform[] = ['linkedin', 'twitter', 'tiktok', 'instagram', 'youtube_shorts', 'youtube'];

export function CreateDraftModal({ contentId, onClose, onCreated }: CreateDraftModalProps) {
  const [platform, setPlatform] = useState<Platform>('linkedin');
  const [textContent, setTextContent] = useState('');
  const [hashtags, setHashtags] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!textContent.trim()) {
      setError('Post content is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await api.createDraft({
        contentId,
        platform,
        textContent: textContent.trim(),
        platformMetadata: {
          hashtags: hashtags
            .split(/[\s,#]+/)
            .filter(Boolean)
            .map((t) => t.replace(/^#/, '')),
        },
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create draft');
      setLoading(false);
    }
  };

  const platformInfo = PLATFORM_INFO[platform] || { name: platform, icon: "📱", color: "bg-gray-500", maxTextLength: 500 };

  // Character limits
  const maxChars: Record<Platform, number> = {
    linkedin: 3000,
    twitter: 280,
    tiktok: 2200,
    instagram: 2200,
    youtube_shorts: 100,
    youtube: 5000,
    snapchat: 250,
  };

  const charLimit = maxChars[platform];
  const charCount = textContent.length;
  const isOverLimit = charCount > charLimit;

  return (
    <Modal isOpen onClose={onClose} title="Create Draft" size="lg">
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Platform selector */}
        <div>
          <label className="label mb-2">Platform</label>
          <div className="grid grid-cols-5 gap-2">
            {PLATFORMS.map((p) => {
              const info = PLATFORM_INFO[p]; if (!info) return null;
              const isSelected = p === platform;
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPlatform(p)}
                  className={`flex flex-col items-center gap-1 rounded-lg border-2 p-3 text-center transition-all ${
                    isSelected
                      ? 'border-brand-green bg-brand-green-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <span className="text-xl">{info.icon}</span>
                  <span className="text-xs font-medium text-gray-700">
                    {info.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Post content */}
        <div>
          <div className="flex items-center justify-between">
            <label className="label">
              {platform === 'youtube_shorts' ? 'Title' : 'Post Content'}
            </label>
            <span
              className={`text-xs ${isOverLimit ? 'font-bold text-red-600' : 'text-gray-400'}`}
            >
              {charCount}/{charLimit}
            </span>
          </div>
          <textarea
            value={textContent}
            onChange={(e) => setTextContent(e.target.value)}
            rows={platform === 'twitter' ? 3 : 6}
            className={`input mt-1 resize-none ${isOverLimit ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
            placeholder={
              platform === 'youtube_shorts'
                ? 'Enter a catchy title for your Short...'
                : `Write your ${platformInfo.name} post...`
            }
            autoFocus
          />
        </div>

        {/* Hashtags */}
        {platform !== 'youtube_shorts' && (
          <div>
            <label className="label">Hashtags</label>
            <input
              type="text"
              value={hashtags}
              onChange={(e) => setHashtags(e.target.value)}
              className="input mt-1"
              placeholder="#mortgage #homebuying #realestate"
            />
            <p className="mt-1 text-xs text-gray-400">
              Separate with spaces or commas
            </p>
          </div>
        )}

        {/* Error */}
        {error && (
          <p className="text-sm font-medium text-red-600">{error}</p>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || isOverLimit || !textContent.trim()}
            className="btn-primary"
          >
            {loading ? 'Creating...' : 'Create Draft'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
